import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { MaskedLines, Reveal, Stagger, StaggerItem } from "@/components/Reveal";
import Counter from "@/components/Counter";
import {
  IMG,
  HIGHLIGHTS,
  SERVICES,
  STATS,
  TESTIMONIALS,
} from "@/data/content";

const MANIFESTO = [
  {
    no: "01",
    title: "From Soil to Soilless",
    body: "Whether you're starting your first hydroponic setup, scaling a commercial polyhouse, or need expert hands to manage your farm from seed to harvest — we bring science-backed agronomy and hands-on execution together.",
  },
  {
    no: "02",
    title: "Farms That Work",
    body: "So your farm doesn't just grow crops, it grows profit. We build systems around your land, climate, and market — never a one-size-fits-all template.",
  },
  {
    no: "03",
    title: "One Partner, Every Stage",
    body: "Consultancy, construction, and management under one roof. No juggling multiple vendors — we stay involved well beyond installation, through every crop cycle.",
  },
];

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const overlayY = useTransform(scrollYProgress, [0, 1], ["0%", "-20%"]);

  return (
    <section
      ref={ref}
      data-testid="home-hero"
      className="relative h-[100svh] min-h-[640px] overflow-hidden"
    >
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img
          src="https://blogger.googleusercontent.com/img/b/R29vZ2xl/AVvXsEg1G4GHR47K2pelGvdaRhmEqNUuMNEPuwW59b2xelpuXmoiFJfy51ezd4tSLZrFb9HitST_5Amu37vJi0HaJeB7ONyzVV1BbBZdE0kiwslFgYLMLCYLKXFFDkQUbWlKd6aYbYtdV8ecLgd3vTY2RtzaZ0y_iSVIVUHJSXVzYjLkV5EDB2R55jA6ot3u-pY/s320/6.png"
          alt="Modern polyhouse greenhouse farming"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/55 to-background/40" />
        <div className="grain absolute inset-0 opacity-20" />
      </motion.div>

      <motion.div
        style={{ y: overlayY }}
        className="relative h-full mx-auto max-w-[1400px] px-5 md:px-10 flex flex-col justify-end pb-16 md:pb-24"
      >
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="overline text-primary mb-6"
        >
          Growing Smarter · Growing Together
        </motion.p>

        <h1 className="font-clash font-semibold tracking-tight text-[2.6rem] leading-[0.98] sm:text-6xl lg:text-[5.5rem] max-w-5xl">
          <MaskedLines
            lines={[
              "Cultivating the Future",
              "of Farming — One Smart",
              "Solution at a Time",
            ]}
            delay={0.3}
          />
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.9 }}
          className="mt-8 max-w-2xl text-base md:text-lg text-foreground/75 leading-relaxed"
        >
          India's trusted partner for hydroponics agronomy consultancy, farm
          management, polyhouse construction, and end-to-end agricultural services
          — grow more with less land, less water, and more precision.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.1 }}
          className="mt-10 flex flex-col sm:flex-row gap-4"
        >
          <Link
            to="/contact"
            data-testid="hero-cta-consultation"
            className="group overline inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground px-8 py-4 font-bold transition-transform duration-300 hover:scale-[1.04]"
          >
            Book a Free Consultation
            <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
          <Link
            to="/services"
            data-testid="hero-cta-services"
            className="overline inline-flex items-center justify-center gap-2 border border-border text-foreground px-8 py-4 font-bold hover:border-primary hover:text-primary transition-colors duration-300"
          >
            Explore Our Services
          </Link>
        </motion.div>
      </motion.div>
    </section>
  );
}

function MarqueeStrip() {
  const items = [
    "Hydroponics",
    "Polyhouse Construction",
    "Farm Management",
    "Agronomy Advisory",
    "Training",
    "Turnkey Projects",
  ];
  return (
    <div className="border-y border-border bg-primary text-primary-foreground py-5">
      <Marquee speed={40} gradient={false}>
        {items.map((t, i) => (
          <span key={i} className="marquee-item font-clash text-xl md:text-2xl font-medium mx-8">
            {t}
            <span className="mx-8 text-primary-foreground/50">✦</span>
          </span>
        ))}
      </Marquee>
    </div>
  );
}

function IntroStrip() {
  return (
    <section className="mx-auto max-w-[1400px] px-5 md:px-10 py-24 md:py-32 border-b border-border">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-start">
        <Reveal className="md:col-span-5">
          <p className="overline text-primary">The FarmingWave Way</p>
        </Reveal>
        <Reveal delay={0.1} className="md:col-span-7">
          <h2 className="font-clash text-3xl md:text-4xl lg:text-5xl leading-tight tracking-tight">
            From Soil to Soilless — We Build Farms That{" "}
            <span className="text-primary">Work.</span>
          </h2>
          <p className="mt-8 text-lg text-muted-foreground leading-relaxed max-w-2xl">
            Whether you're starting your first hydroponic setup, scaling a
            commercial polyhouse, or need expert hands to manage your farm from
            seed to harvest, FarmingWave brings science-backed agronomy and
            hands-on execution together — so your farm doesn't just grow crops,
            it grows profit.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function Highlights() {
  return (
    <section className="border-b border-border" data-testid="home-highlights">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10 py-24 md:py-32">
        <Reveal>
          <p className="overline text-primary">Why FarmingWave</p>
          <h2 className="mt-4 font-clash text-3xl md:text-4xl lg:text-5xl tracking-tight max-w-2xl">
            Built on expertise. Measured in yield.
          </h2>
        </Reveal>
        <Stagger className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 border-t border-l border-border">
          {HIGHLIGHTS.map((h) => (
            <StaggerItem key={h.title}>
              <div className="group h-full p-8 border-r border-b border-border hover:bg-accent/40 transition-colors duration-400">
                <h.icon className="w-9 h-9 text-primary" strokeWidth={1.6} />
                <h3 className="mt-8 font-clash text-xl">{h.title}</h3>
                <p className="mt-4 text-muted-foreground leading-relaxed">{h.text}</p>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>
  );
}

function Manifesto() {
  return (
    <section className="border-b border-border" data-testid="home-manifesto">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10 py-24 md:py-32">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <div className="md:sticky md:top-32">
              <p className="overline text-primary">Our Approach</p>
              <h2 className="mt-4 font-clash text-3xl md:text-4xl lg:text-5xl tracking-tight leading-tight">
                Science-backed. Profit-focused.
              </h2>
              <img
                src="https://blogger.googleusercontent.com/img/b/R29vZ2xl/AVvXsEjCm54pE0slbSYRSeKQYxnXFIgrQNIRemrMPPKxTtM4cupmNdjuQcT4ukNwKERT_8K9JBC_fwqyANjwCZFsfq8wohP2mncKyDxF5vQFXj69XNlmDaz8-x2l0tbTzByU5e_gKxH7nrrDjp6Btw6GgYnyBoHFc3F38o-oksTCCsM6XuOSJ4b5_iKXaFCbBpM/s320/White%20Classic%20Organic%20Spiti%20Valley%20Himachal%20Travel%20Instagram%20Post%20(3).png"
                alt="Hydroponic lettuce cultivation"
                className="mt-10 w-full aspect-[4/3] object-cover hidden md:block"
              />
            </div>
          </div>
          <div className="md:col-span-7 md:col-start-6 flex flex-col">
            {MANIFESTO.map((m) => (
              <Reveal key={m.no}>
                <div className="py-12 border-t border-border first:border-t-0 md:first:border-t">
                  <span className="font-mono text-primary text-sm">{m.no}</span>
                  <h3 className="mt-4 font-clash text-2xl md:text-3xl">{m.title}</h3>
                  <p className="mt-5 text-lg text-muted-foreground leading-relaxed">
                    {m.body}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function ServicesSnapshot() {
  return (
    <section className="border-b border-border" data-testid="home-services">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10 pt-24 md:pt-32">
        <Reveal className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div>
            <p className="overline text-primary">What We Do</p>
            <h2 className="mt-4 font-clash text-3xl md:text-4xl lg:text-5xl tracking-tight max-w-xl">
              Complete solutions under one roof.
            </h2>
          </div>
          <Link
            to="/services"
            data-testid="services-view-all"
            className="overline inline-flex items-center gap-2 text-primary hover:gap-3 transition-all duration-300"
          >
            View all services <ArrowRight className="w-4 h-4" />
          </Link>
        </Reveal>
      </div>
      <div className="mx-auto max-w-[1400px] px-5 md:px-10 mt-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-t border-l border-border">
          {SERVICES.map((s, i) => (
            <Reveal key={s.id} delay={(i % 3) * 0.08}>
              <Link
                to="/services"
                data-testid={`service-card-${s.id}`}
                className="group flex flex-col h-full p-8 border-r border-b border-border hover:bg-accent/40 transition-colors duration-400"
              >
                <div className="flex items-start justify-between">
                  <s.icon className="w-8 h-8 text-primary" strokeWidth={1.6} />
                  <ArrowUpRight className="w-5 h-5 text-muted-foreground transition-all duration-300 group-hover:text-primary group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </div>
                <h3 className="mt-8 font-clash text-xl leading-snug">{s.title}</h3>
                <p className="mt-4 text-muted-foreground leading-relaxed">{s.teaser}</p>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
      <div className="pb-24 md:pb-32" />
    </section>
  );
}

function Stats() {
  return (
    <section
      className="relative border-b border-border overflow-hidden"
      data-testid="home-stats"
    >
      <div className="absolute inset-0">
        <img src={IMG.agriDrone} alt="" className="w-full h-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-background/80" />
      </div>
      <div className="relative mx-auto max-w-[1400px] px-5 md:px-10 py-24 md:py-32">
        <Reveal>
          <p className="overline text-primary">Impact in Numbers</p>
        </Reveal>
        <div className="mt-14 grid grid-cols-2 lg:grid-cols-4 border-t border-l border-border">
          {STATS.map((s) => (
            <Reveal key={s.label}>
              <div className="p-8 border-r border-b border-border">
                <div className="font-clash text-5xl md:text-6xl lg:text-7xl text-primary tracking-tight">
                  <Counter to={s.value} suffix={s.suffix} />
                </div>
                <p className="mt-4 text-muted-foreground">{s.label}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Testimonials() {
  return (
    <section className="border-b border-border" data-testid="home-testimonials">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10 py-24 md:py-32">
        <Reveal>
          <p className="overline text-primary">What Our Growers Say</p>
          <h2 className="mt-4 font-clash text-3xl md:text-4xl lg:text-5xl tracking-tight max-w-2xl">
            Trusted across India's farms.
          </h2>
        </Reveal>
        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-px bg-border border border-border">
          {TESTIMONIALS.map((t) => (
            <Reveal key={t.name} className="bg-background">
              <figure className="h-full p-8 md:p-10 flex flex-col justify-between">
                <blockquote className="font-clash text-xl md:text-2xl leading-snug text-foreground/90">
                  "{t.quote}"
                </blockquote>
                <figcaption className="mt-10">
                  <div className="font-semibold">{t.name}</div>
                  <div className="text-muted-foreground text-sm">{t.location}</div>
                  <div className="overline text-primary mt-2">{t.detail}</div>
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function FinalCTA() {
  return (
    <section
      className="relative overflow-hidden"
      data-testid="home-final-cta"
    >
      <div className="mx-auto max-w-[1400px] px-5 md:px-10 py-28 md:py-40">
        <Reveal>
          <p className="overline text-primary">Let's Build</p>
          <h2 className="mt-6 font-clash text-4xl sm:text-6xl lg:text-7xl tracking-tight leading-[0.98] max-w-4xl">
            Ready to build a farm that works{" "}
            <span className="text-primary">smarter?</span>
          </h2>
          <p className="mt-8 max-w-2xl text-lg text-muted-foreground leading-relaxed">
            Let's design a farming solution tailored to your land, budget, and
            goals.
          </p>
          <Link
            to="/contact"
            data-testid="final-cta-button"
            className="group mt-12 overline inline-flex items-center gap-3 bg-primary text-primary-foreground px-10 py-5 font-bold transition-transform duration-300 hover:scale-[1.04]"
          >
            Get a Free Site Assessment
            <ArrowUpRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <div data-testid="home-page">
      <Hero />
      <MarqueeStrip />
      <IntroStrip />
      <Highlights />
      <Manifesto />
      <ServicesSnapshot />
      <Stats />
      <Testimonials />
      <FinalCTA />
    </div>
  );
}
